import * as React from 'react'
import { Link } from 'react-router-dom'

import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { F0AvatarPerson } from '@factorialco/f0-react'
import { Clock } from '@factorialco/f0-react/icons/app'
import { TimePlanningBalance } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'

import TooltipIcon from 'modules/shift_management/components/ShiftManagement/TooltipIcon'
import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import type { ShiftsRow } from 'modules/shift_management/lib/types'

import BankOfHours from './BankOfHours'
import HoursToPlan from './HoursToPlan'
import PlannedHours from './PlannedHours'
import ShiftsRowCheckbox from './ShiftsRowCheckbox'

interface Props {
  shiftsRow: ShiftsRow
  balances?: TimePlanningBalance
  isOverMaximum: boolean
  plannedHoursTooltipCopy: string
}

const EmployeeInfoCell = ({
  shiftsRow,
  balances,
  isOverMaximum,
  plannedHoursTooltipCopy,
}: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const [{ hasFeatureFlag }] = useEnabledFeatures()

  const [isHovered, setIsHovered] = React.useState(false)
  const { viewOptions } = useCurrentViewOptions()
  const { navigation } = useNavigation()
  const {
    employee: { showRole, showPlanningBalance, showAvatar, showBankOfHours },
  } = viewOptions
  const hasBankOfHoursEnabled = hasFeatureFlag('dev_time_planning_bank_of_hours')

  const visibleRole = !!shiftsRow.job && showRole

  const [firstName, lastName] = shiftsRow.title.split(' ')

  return (
    <Box flexDirection="row" justifyContent="spaceBetween" flexGrow minWidth="s0" gap="s8">
      <Box
        flexDirection="row"
        alignItems="flexStart"
        gap="s8"
        justifyContent="center"
        minWidth="s0"
        flexGrow
      >
        {showAvatar && (
          <F0AvatarPerson
            firstName={firstName || ''}
            lastName={lastName || ''}
            size="md"
            src={buildUrl(shiftsRow.avatar)}
          />
        )}
        <Box
          flexDirection="column"
          gap={visibleRole ? 's8' : 's0'}
          paddingLeft={showAvatar ? 's0' : 's8'}
          flexGrow
          minWidth="s0"
        >
          <Box
            flexDirection="column"
            justifyContent="center"
            height={!visibleRole && !showPlanningBalance ? 's32' : 'fit-content'}
            maxWidth="s140"
          >
            <Box flexDirection="row" alignItems="center" gap="s4">
              <Link
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                to={navigation.employees.id.fullPath({ id: shiftsRow.id })}
                style={{ textDecoration: 'none' }}
                target="_blank"
              >
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s4"
                  minWidth="s0"
                  maxWidth="s140"
                  flexGrow
                >
                  <Text
                    size="200"
                    weight="medium"
                    color={isHovered ? 'viridian1000' : 'grey1000'}
                    ellipsis
                  >
                    {shiftsRow.title}
                  </Text>
                </Box>
              </Link>
              {isOverMaximum && (
                <TooltipIcon.Small
                  icon="alert-circle"
                  color="radicalRed1000"
                  label={plannedHoursTooltipCopy}
                />
              )}
            </Box>
            {visibleRole && (
              <Text size="100" color="grey800" ellipsis>
                {shiftsRow.job}
              </Text>
            )}
          </Box>
          <Box gap="s6">
            {showPlanningBalance && (
              <Box flexDirection="row" height="s16" alignItems="center" gap="s4">
                <F0Icon icon={Clock} size="xs" className="text-f1-icon-secondary" />
                <PlannedHours shiftsRow={shiftsRow} />
                {(shiftsRow.canManage || shiftsRow.id === currentEmployee?.id) && (
                  <>
                    <Text size="100" color="grey800">
                      /
                    </Text>

                    {balances ? (
                      <HoursToPlan balances={balances} />
                    ) : (
                      <Skeleton isLoading height="s16" width="s40" />
                    )}
                  </>
                )}
              </Box>
            )}
            {shiftsRow.canManage && hasBankOfHoursEnabled && showBankOfHours && (
              <BankOfHours shiftsRow={shiftsRow} />
            )}
          </Box>
        </Box>
      </Box>
      <ShiftsRowCheckbox shiftsRow={shiftsRow} />
    </Box>
  )
}

export default EmployeeInfoCell
